


































































import { Component, Vue } from 'vue-property-decorator'
import ApiClient from '@/services/api-client'
import { CvValue, Resource } from '@/models'
import { Utils } from '@/utils'
import UpsertResourceModal from '@/components/modals/UpsertResourceModal.vue'

const CATEGORY_CV = 'rsca'

@Component({
  components: { UpsertResourceModal }
})
export default class Resources extends Vue {
  private resources: Resource[] = []
  private categories: CvValue[] = []
  private loading = false
  private searchFilter = ''
  private selectedResource: Resource | null = null

  private fields = [
    { key: 'code', label: this.$t('resources_view.columns.code'), sortable: true },
    { key: 'name', label: this.$t('resources_view.columns.name'), sortable: true },
    { key: 'category', label: this.$t('resources_view.columns.category'), sortable: true },
    { key: 'active', label: this.$t('resources_view.columns.active'), sortable: true, class: 'text-center' },
    { key: 'actions', label: this.$t('table.actions'), sortable: false, class: 'text-center' }
  ]

  get filteredResources(): Resource[] {
    if (!this.searchFilter.trim()) return this.resources
    const f = Utils.normalizeText(this.searchFilter)
    return this.resources.filter(r =>
      Utils.normalizeText(r.code).includes(f) ||
      Utils.normalizeText(r.name).includes(f) ||
      Utils.normalizeText(this.categoryName(r.category)).includes(f)
    )
  }

  categoryName(codeId: string): string {
    return this.categories.find(c => c.codeId === codeId)?.name || codeId
  }

  async created() {
    try {
      // noCache: the CV endpoint sends a long Cache-Control; bust it so newly seeded
      // categories show up without a hard browser reload.
      this.categories = await ApiClient.getCvValuesByType(CATEGORY_CV, true)
    } catch (error) {
      console.error('Error loading resource categories:', error)
    }
    await this.loadResources()
  }

  async loadResources() {
    try {
      this.loading = true
      this.resources = await ApiClient.getResources()
    } catch (error) {
      console.error('Error loading resources:', error)
      this.showError(this.$t('resources_view.error_loading').toString())
    } finally {
      this.loading = false
    }
  }

  openCreateModal() {
    this.selectedResource = null
    this.$nextTick(() => this.$bvModal.show('UpsertResourceModal'))
  }

  openEditModal(resource: Resource) {
    this.selectedResource = { ...resource }
    this.$nextTick(() => this.$bvModal.show('UpsertResourceModal'))
  }

  async onSaved() {
    await this.loadResources()
    this.showSuccess(this.$t('resources_view.saved_success').toString())
  }

  async confirmDelete(resource: Resource) {
    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('resources_view.confirm_delete', { name: resource.name }).toString(),
      {
        title: this.$t('resources_view.confirm_delete_title').toString(),
        okVariant: 'danger',
        okTitle: this.$t('button_actions.remove').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )
    if (!confirmed || resource.id == null) return
    try {
      await ApiClient.deleteResource(resource.id)
      await this.loadResources()
      this.showSuccess(this.$t('resources_view.deleted_success').toString())
    } catch (error: any) {
      console.error('Error deleting resource:', error)
      this.showError(error.message || this.$t('resources_view.error_deleting').toString())
    }
  }

  private showSuccess(message: string) {
    this.$bvToast.toast(message, {
      title: this.$t('feedback.feedback_message_success_title').toString(),
      variant: 'success',
      solid: true,
      autoHideDelay: 2000
    })
  }

  private showError(message: string) {
    this.$bvToast.toast(message, {
      title: this.$t('feedback.error_title_generic').toString(),
      variant: 'danger',
      solid: true
    })
  }
}
